import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'

export const useThemeStore = defineStore('theme', () => {
  // Theme state
  const isDark = ref(false)
  const systemPreference = ref('light') // 'light', 'dark', or 'system'

  // Computed properties
  const currentTheme = computed(() => {
    if (systemPreference.value === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
    }
    return systemPreference.value
  })

  const isDarkMode = computed(() => currentTheme.value === 'dark')

  // Theme colors for CSS custom properties
  const lightTheme = {
    '--bg-primary': '#ffffff',
    '--bg-secondary': '#f8fafc',
    '--bg-tertiary': '#f1f5f9',
    '--text-primary': '#1e293b',
    '--text-secondary': '#64748b',
    '--text-tertiary': '#94a3b8',
    '--border-primary': '#e2e8f0',
    '--border-secondary': '#cbd5e1',
    '--shadow-sm': '0 1px 2px 0 rgb(0 0 0 / 0.05)',
    '--shadow-md': '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
    '--shadow-lg': '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
    '--accent-primary': '#3b82f6',
    '--accent-secondary': '#1d4ed8',
    '--success': '#10b981',
    '--warning': '#f59e0b',
    '--error': '#ef4444',
    '--info': '#06b6d4',
  }

  const darkTheme = {
    '--bg-primary': '#0A0F1E',
    '--bg-secondary': '#121829',
    '--bg-tertiary': '#1A1F33',
    '--text-primary': '#ffffff',
    '--text-secondary': '#9ca3af',
    '--text-tertiary': '#6b7280',
    '--border-primary': '#334155',
    '--border-secondary': '#475569',
    '--shadow-sm': '0 1px 2px 0 rgb(0 0 0 / 0.3)',
    '--shadow-md': '0 4px 6px -1px rgb(0 0 0 / 0.4), 0 2px 4px -2px rgb(0 0 0 / 0.4)',
    '--shadow-lg': '0 10px 15px -3px rgb(0 0 0 / 0.4), 0 4px 6px -4px rgb(0 0 0 / 0.4)',
    '--accent-primary': '#6BCF7E',
    '--accent-secondary': '#5ABF6D',
    '--success': '#34d399',
    '--warning': '#fbbf24',
    '--error': '#f87171',
    '--info': '#22d3ee',
  }

  // Actions
  const setTheme = (theme) => {
    systemPreference.value = theme
    isDark.value = theme === 'dark'
    applyTheme()
    saveThemePreference()
  }

  const toggleTheme = () => {
    const newTheme = isDarkMode.value ? 'light' : 'dark'
    setTheme(newTheme)
  }

  const applyTheme = () => {
    const root = document.documentElement
    const theme = isDarkMode.value ? darkTheme : lightTheme

    // Apply CSS custom properties
    Object.entries(theme).forEach(([property, value]) => {
      root.style.setProperty(property, value)
    })

    // Apply Tailwind dark mode class
    if (isDarkMode.value) {
      root.classList.add('dark')
    } else {
      root.classList.remove('dark')
    }

    // Update meta theme-color for mobile browsers
    const metaThemeColor = document.querySelector('meta[name="theme-color"]')
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', isDarkMode.value ? '#0f172a' : '#ffffff')
    }
  }

  const saveThemePreference = () => {
    localStorage.setItem('theme-preference', systemPreference.value)
  }

  const loadThemePreference = () => {
    const saved = localStorage.getItem('theme-preference')
    if (saved && ['light', 'dark', 'system'].includes(saved)) {
      systemPreference.value = saved
    } else {
      // Default to dark mode
      systemPreference.value = 'dark'
    }
    isDark.value = isDarkMode.value
    applyTheme()
  }

  const initializeTheme = () => {
    loadThemePreference()

    // Listen for system theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handleSystemThemeChange = () => {
      if (systemPreference.value === 'system') {
        isDark.value = isDarkMode.value
        applyTheme()
      }
    }

    mediaQuery.addEventListener('change', handleSystemThemeChange)

    // Cleanup function
    return () => {
      mediaQuery.removeEventListener('change', handleSystemThemeChange)
    }
  }

  // Watch for theme changes to update Tailwind classes
  watch(isDarkMode, (newValue) => {
    isDark.value = newValue
  })

  return {
    // State
    isDark,
    systemPreference,

    // Computed
    currentTheme,
    isDarkMode,

    // Actions
    setTheme,
    toggleTheme,
    applyTheme,
    initializeTheme,
    loadThemePreference,
  }
})
